import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Form, Input, Select, Button, Space, Spin, Alert, message } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import {
  getSocialAccount,
  createSocialAccount,
  updateSocialAccount,
  type CreateSocialAccountDto,
  type UpdateSocialAccountDto,
} from '@/api/socialApi';
import { useAuth } from '@/hooks/useAuth';

const PLATFORM_OPTIONS = ['facebook', 'instagram', 'twitter', 'linkedin', 'wechat'];

interface AccountFormValues {
  platform: string;
  account_name: string;
  account_id: string;
  access_token?: string;
  refresh_token?: string;
  token_expires_at?: string;
  profile_url?: string;
  avatar_url?: string;
}

export default function SocialAccountForm() {
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const [form] = Form.useForm<AccountFormValues>();

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isEdit || !id) return;
    setLoading(true);
    setError(null);
    getSocialAccount(id)
      .then((account) => {
        form.setFieldsValue({
          platform: account.platform,
          account_name: account.account_name,
          account_id: account.account_id,
          access_token: undefined,
          refresh_token: undefined,
          token_expires_at: account.token_expires_at || undefined,
          profile_url: account.profile_url || undefined,
          avatar_url: account.avatar_url || undefined,
        });
      })
      .catch((err) => setError(err instanceof Error ? err.message : t('app.not_found')))
      .finally(() => setLoading(false));
  }, [isEdit, id, form, t]);

  const handleSubmit = async (values: AccountFormValues) => {
    setSubmitting(true);
    setError(null);
    try {
      if (isEdit && id) {
        const dto: UpdateSocialAccountDto = {
          account_name: values.account_name,
          access_token: values.access_token,
          refresh_token: values.refresh_token,
          token_expires_at: values.token_expires_at,
          profile_url: values.profile_url,
          avatar_url: values.avatar_url,
        };
        await updateSocialAccount(id, dto);
        message.success(t('social.update_account_success'));
        navigate(`/social/accounts/${id}`);
      } else {
        const dto: CreateSocialAccountDto = {
          platform: values.platform,
          account_name: values.account_name,
          account_id: values.account_id,
          access_token: values.access_token,
          refresh_token: values.refresh_token,
          token_expires_at: values.token_expires_at,
          profile_url: values.profile_url,
          avatar_url: values.avatar_url,
        };
        const created = await createSocialAccount(dto);
        message.success(t('social.create_account_success'));
        navigate(`/social/accounts/${created.id}`);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('app.error'));
    } finally {
      setSubmitting(false);
    }
  };

  if (!hasPermission('social_accounts', 'write')) {
    return <Alert type="error" message={t('app.error')} />;
  }

  if (loading) return <Spin size="large" />;

  return (
    <div>
      <Space style={{ marginBottom: 16 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/social')}>
          {t('app.back')}
        </Button>
      </Space>

      <h2>{isEdit ? t('social.edit_account_title') : t('social.create_account_title')}</h2>

      {error && <Alert type="error" message={error} style={{ marginBottom: 16 }} />}

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{ platform: 'facebook' }}
        style={{ maxWidth: 700 }}
      >
        <Form.Item
          name="platform"
          label={t('social.field.platform')}
          rules={[{ required: true, message: t('social.validation.platform_required') }]}
        >
          <Select disabled={isEdit}>
            {PLATFORM_OPTIONS.map((opt) => (
              <Select.Option key={opt} value={opt}>
                {t(`social.platform.${opt}`)}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          name="account_name"
          label={t('social.field.account_name')}
          rules={[
            { required: true, message: t('social.validation.account_name_required') },
            { max: 200, message: t('social.validation.account_name_max_length') },
          ]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          name="account_id"
          label={t('social.field.account_id')}
          rules={[{ required: !isEdit, message: t('social.validation.account_id_required') }]}
        >
          <Input disabled={isEdit} />
        </Form.Item>

        <Form.Item name="access_token" label={t('social.field.access_token')}>
          <Input.Password />
        </Form.Item>

        <Form.Item name="refresh_token" label={t('social.field.refresh_token')}>
          <Input.Password />
        </Form.Item>

        <Form.Item name="token_expires_at" label={t('social.field.token_expires_at')}>
          <Input placeholder={t('social.field.token_expires_at')} />
        </Form.Item>

        <Form.Item name="profile_url" label={t('social.field.profile_url')}>
          <Input />
        </Form.Item>

        <Form.Item name="avatar_url" label={t('social.field.avatar_url')}>
          <Input />
        </Form.Item>

        <Form.Item>
          <Space>
            <Button type="primary" htmlType="submit" loading={submitting}>
              {t('app.save')}
            </Button>
            <Button onClick={() => navigate('/social')}>{t('app.cancel')}</Button>
          </Space>
        </Form.Item>
      </Form>
    </div>
  );
}
